import React, { useCallback, useMemo } from 'react';
import {
  Row, Col, Popover,
  Divider,
} from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import BorderlessButton from '../../common/buttons/BorderlessButton';

import { DAYS } from './formTrigger.constants';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';

function WorkflowTriggerCheckbox({
  onCheckChange,
  checked,
  disabled,
  showTimeOptions,
  triggerDays = [],
  onTriggerDaysChanged,
  triggerRequiredSubmit,
  onRequiredSubmitChanged,
  title,
}) {
  const triggerDaySet = useMemo(() => new Set(triggerDays), [triggerDays]);
  const onChange = useCallback((key) => (newChecked) => {
    const changeFn = key === 'requiredSubmit' ? onRequiredSubmitChanged : onCheckChange;
    if (!changeFn) return;
    changeFn(newChecked);
  }, [onCheckChange, onRequiredSubmitChanged]);

  const onDayChange = useCallback((day) => (newChecked) => {
    if (!onTriggerDaysChanged) return;
    const newDays = newChecked
      ? triggerDays.concat([day])
      : triggerDays.filter((existingDay) => existingDay !== day);
    onTriggerDaysChanged(newDays);
  }, [onTriggerDaysChanged, triggerDays]);

  return (
    <Row style={{ margin: '5px 5px', width: '100%' }}>
      <Col>
        <OnTraccrCheckbox
          value={checked}
          onChange={onChange('enable')}
          textStyle={{ color: 'white' }}
          disabled={disabled}
          label={title}
        />
      </Col>
      {
        showTimeOptions
        && (
        <Col>
          <Popover
            placement="right"
            trigger={['click']}
            content={(
              <div style={{ maxWidth: 300 }}>
                <Row>
                  <OnTraccrCheckbox
                    style={{ fontWeight: 'bold' }}
                    value={triggerRequiredSubmit}
                    onChange={onChange('requiredSubmit')}
                    label="Enable Mandatory Form Submission"
                  />
                </Row>
                <Divider style={{ marginBottom: 10, marginTop: 10 }} />
                <Row style={{ fontWeight: 'bold', marginBottom: 10 }}>
                  Days to Trigger
                </Row>
                <Row>
                  {DAYS.map((day) => (
                    <Col span={12} key={day} style={{ marginTop: 5 }}>
                      <OnTraccrCheckbox
                        value={triggerDaySet.has(day)}
                        onChange={onDayChange(day)}
                        style={{ marginLeft: 8 }}
                        label={day}
                      />
                    </Col>
                  ))}
                </Row>
              </div>
            )}
          >
            <BorderlessButton
              style={{
                height: 20, padding: 0, background: 'transparent', color: 'white',
              }}
              iconNode={<SettingOutlined />}
            />
          </Popover>
        </Col>
        )
      }
    </Row>
  );
}

WorkflowTriggerCheckbox.propTypes = {
  onCheckChange: PropTypes.func,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  showTimeOptions: PropTypes.bool.isRequired,
  triggerDays: PropTypes.arrayOf(PropTypes.string),
  onTriggerDaysChanged: PropTypes.func,
  triggerRequiredSubmit: PropTypes.bool,
  onRequiredSubmitChanged: PropTypes.func,
  title: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]).isRequired,
};

WorkflowTriggerCheckbox.defaultProps = {
  onCheckChange: null,
  disabled: false,
  triggerDays: [],
  onTriggerDaysChanged: null,
  triggerRequiredSubmit: false,
  onRequiredSubmitChanged: null,
};

export default WorkflowTriggerCheckbox;
